import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, EXPENSE_CATEGORIES, FIXED_COST_CATEGORIES, INTERACTION_TYPES, SOURCES, STATUS_LABELS, VAT_STATUS, JUDETE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DualMoneyInput from "@/components/DualMoneyInput";
import DateInput from "@/components/DateInput";
import { Paperclip, Car as CarIcon, Building2 } from "lucide-react";

const TITLES = {
  car: "Adaugă mașină nouă",
  client: "Adaugă client nou",
  expense: "Adaugă cheltuială",
  interaction: "Adaugă interacțiune",
};

export default function QuickAddDialog({ kind, onClose }) {
  const open = !!kind;
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [cars, setCars] = useState([]);
  const [clients, setClients] = useState([]);
  const [expType, setExpType] = useState("car");  // car | fixed (only for expense)

  useEffect(() => {
    if (!open) { setForm({}); setExpType("car"); return; }
    if (kind === "expense") {
      setForm({ vat_status: "cu_tva", category: "Reparații" });
      api.get("/cars").then((r) => setCars(r.data)).catch(() => {});
    }
    if (kind === "interaction") {
      api.get("/cars").then((r) => setCars(r.data)).catch(() => {});
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }
  }, [open, kind]);

  // Reset category default when toggling expense type
  useEffect(() => {
    if (kind === "expense") {
      setForm((f) => ({
        ...f,
        category: expType === "car" ? "Reparații" : "Chirie",
        car_id: expType === "fixed" ? null : f.car_id,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expType]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function pickInvoice(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("invoice_path", data.path);
      set("invoice_filename", data.filename);
      toast.success("Document atașat");
    } catch { toast.error("Upload eșuat"); }
    finally { e.target.value = ""; }
  }

  async function submit() {
    setLoading(true);
    try {
      if (kind === "car") {
        const body = {
          make: form.make || "", model: form.model || "",
          year: Number(form.year) || null,
          vin: form.vin || "",
          purchase_price: Number(form.purchase_price) || 0,
          asking_price: Number(form.asking_price) || 0,
          status: form.status || "in_pregatire",
          notes: form.notes || "",
          purchase_date: form.purchase_date || null,
        };
        if (!body.make || !body.model) throw new Error("Completează marca și modelul");
        if (!body.purchase_date) throw new Error("Completează data achiziției");
        await api.post("/cars", body);
        toast.success("Mașină adăugată");
      } else if (kind === "client") {
        if (!form.name) throw new Error("Completează numele");
        await api.post("/clients", {
          name: form.name, phone: form.phone || "", email: form.email || "",
          source: form.source || "", notes: form.notes || "",
          county: form.county || "", city: form.city || "",
          interest_car_id: form.interest_car_id || null,
        });
        toast.success("Client adăugat");
      } else if (kind === "expense") {
        if (!form.amount) throw new Error("Completează suma");
        const payload = {
          category: form.category || "Altele",
          amount: Number(form.amount),
          description: form.description || "",
          invoice_path: form.invoice_path || null,
          vat_status: form.vat_status || "cu_tva",
          original_amount: form.original_amount ?? null,
          original_currency: form.original_currency ?? null,
          rate_at_entry: form.rate_at_entry ?? null,
          rate_locked: !!form.rate_locked,
        };
        if (expType === "car") {
          await api.post("/expenses", { ...payload, car_id: form.car_id || null });
        } else {
          await api.post("/fixed-costs", payload);
        }
        toast.success(expType === "car" ? "Cheltuială adăugată" : "Cost fix adăugat");
      } else if (kind === "interaction") {
        if (!form.client_id) throw new Error("Selectează clientul");
        await api.post("/interactions", {
          client_id: form.client_id,
          car_id: form.car_id || null,
          type: form.type || "call",
          note: form.note || "",
        });
        toast.success("Interacțiune adăugată");
      }
      onClose();
      window.dispatchEvent(new CustomEvent("data:refresh"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Eroare");
    } finally {
      setLoading(false);
    }
  }

  const expCategories = expType === "car" ? EXPENSE_CATEGORIES : FIXED_COST_CATEGORIES;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid={`quick-add-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{TITLES[kind] || ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {kind === "car" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Marcă</Label><Input data-testid="qa-car-make" value={form.make || ""} onChange={(e) => set("make", e.target.value)} /></div>
                <div><Label>Model</Label><Input data-testid="qa-car-model" value={form.model || ""} onChange={(e) => set("model", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>An</Label><Input data-testid="qa-car-year" type="number" value={form.year || ""} onChange={(e) => set("year", e.target.value)} /></div>
                <div className="col-span-2"><Label>Data achiziției *</Label><DateInput testid="qa-car-purchase-date" value={form.purchase_date || ""} onChange={(v) => set("purchase_date", v)} /></div>
              </div>
              <div><Label>VIN</Label><Input data-testid="qa-car-vin" value={form.vin || ""} onChange={(e) => set("vin", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Preț achiziție (€)</Label><Input data-testid="qa-car-purchase" type="number" value={form.purchase_price || ""} onChange={(e) => set("purchase_price", e.target.value)} /></div>
                <div><Label>Preț vânzare (€)</Label><Input data-testid="qa-car-asking" type="number" value={form.asking_price || ""} onChange={(e) => set("asking_price", e.target.value)} /></div>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status || "in_pregatire"} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="qa-car-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notițe</Label><Textarea data-testid="qa-car-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
            </>
          )}

          {kind === "client" && (
            <>
              <div><Label>Nume</Label><Input data-testid="qa-client-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Telefon</Label><Input data-testid="qa-client-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
                <div><Label>Email</Label><Input data-testid="qa-client-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Județ</Label>
                  <Select value={form.county || ""} onValueChange={(v) => set("county", v)}>
                    <SelectTrigger data-testid="qa-client-county"><SelectValue placeholder="Alege județul" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {JUDETE.map((j) => (<SelectItem key={j} value={j}>{j}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Localitate</Label><Input data-testid="qa-client-city" placeholder="Ex: Oradea" value={form.city || ""} onChange={(e) => set("city", e.target.value)} /></div>
              </div>
              <div>
                <Label>Sursa lead-ului</Label>
                <Select value={form.source || ""} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="qa-client-source"><SelectValue placeholder="Alege sursa" /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notițe</Label><Textarea data-testid="qa-client-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
            </>
          )}

          {kind === "expense" && (
            <>
              {/* Type toggle */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-full">
                <button
                  type="button"
                  data-testid="qa-exp-type-car"
                  onClick={() => setExpType("car")}
                  className={`text-xs font-medium py-1.5 rounded-full inline-flex items-center justify-center gap-1.5 transition-colors ${expType === "car" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                >
                  <CarIcon className="w-3.5 h-3.5" /> Pe mașină
                </button>
                <button
                  type="button"
                  data-testid="qa-exp-type-fixed"
                  onClick={() => setExpType("fixed")}
                  className={`text-xs font-medium py-1.5 rounded-full inline-flex items-center justify-center gap-1.5 transition-colors ${expType === "fixed" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                >
                  <Building2 className="w-3.5 h-3.5" /> Cost fix
                </button>
              </div>

              {expType === "car" && (
                <div>
                  <Label>Mașină (opțional)</Label>
                  <Select value={form.car_id || ""} onValueChange={(v) => set("car_id", v)}>
                    <SelectTrigger data-testid="qa-exp-car"><SelectValue placeholder="Fără mașină" /></SelectTrigger>
                    <SelectContent>
                      {cars.map((c) => (<SelectItem key={c.id} value={c.id}>{c.make} {c.model} · {c.vin?.slice(-6) || "—"}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Categorie</Label>
                <Select value={form.category || ""} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="qa-exp-category"><SelectValue placeholder="Alege categoria" /></SelectTrigger>
                  <SelectContent>
                    {expCategories.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Sumă</Label>
                <DualMoneyInput
                  testid="qa-exp-money"
                  value={form.amount}
                  onChange={(v) => set("amount", v)}
                  onMeta={(m) => setForm((f) => ({ ...f, ...m }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <Label>TVA</Label>
                  <Select value={form.vat_status || "cu_tva"} onValueChange={(v) => set("vat_status", v)}>
                    <SelectTrigger data-testid="qa-exp-vat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VAT_STATUS.map((v) => (<SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <button
                    type="button"
                    data-testid="qa-exp-attach"
                    onClick={() => document.getElementById("qa-exp-invoice-input").click()}
                    className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-slate-700 border border-slate-200 rounded-lg px-3 py-2 hover:border-slate-400"
                  >
                    <Paperclip className="w-3.5 h-3.5" /> {form.invoice_path ? "Schimbă factura" : "Atașează factură"}
                  </button>
                  <input id="qa-exp-invoice-input" type="file" accept="application/pdf,image/*" capture="environment" className="hidden" onChange={pickInvoice} />
                </div>
              </div>
              {form.invoice_filename && (
                <div className="text-xs text-emerald-700 truncate">✓ {form.invoice_filename}</div>
              )}

              <div><Label>Descriere</Label><Textarea data-testid="qa-exp-desc" rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
            </>
          )}

          {kind === "interaction" && (
            <>
              <div>
                <Label>Client</Label>
                <Select value={form.client_id || ""} onValueChange={(v) => set("client_id", v)}>
                  <SelectTrigger data-testid="qa-int-client"><SelectValue placeholder="Alege clientul" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Mașină (opțional)</Label>
                <Select value={form.car_id || ""} onValueChange={(v) => set("car_id", v)}>
                  <SelectTrigger data-testid="qa-int-car"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {cars.map((c) => (<SelectItem key={c.id} value={c.id}>{c.make} {c.model}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tip</Label>
                <Select value={form.type || "call"} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="qa-int-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INTERACTION_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Notă</Label><Textarea data-testid="qa-int-note" value={form.note || ""} onChange={(e) => set("note", e.target.value)} /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="qa-cancel">Anulează</Button>
          <Button data-testid="qa-submit" onClick={submit} disabled={loading} className="bg-[#0047FF] hover:bg-[#003BD3]">
            {loading ? "Se salvează..." : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
